import { LicenseManager } from '@ag-grid-enterprise/core';
import { NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule, inject } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApolloLink, InMemoryCache, defaultDataIdFromObject } from '@apollo/client/core';
import { NgIdleModule } from '@ng-idle/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NamedOptions, provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { AppRoutingModule } from 'app-routing.module';
import { AppComponent } from 'app.component';
import { AuthenticationModule } from 'authentication/authentication.module';
import { AnonymousGuard } from 'authentication/guards/anonymous.guard';
import { AuthGuard } from 'authentication/guards/auth.guard';
import { TokenInterceptor } from 'authentication/interceptors/token.interceptor';
import { DashboardModule } from 'dashboard/dashboard.module';
import { NgxPermissionsModule } from 'ngx-permissions';
import { BYPASS_ERROR_HANDLER_FOR_GRAPHQL_ERROR_CODES, BypassErrorLink } from 'shared/abstract/bypass-error-link';
import { HeaderComponent, SidebarComponent } from 'shared/components';
import { ContextAwareSidebarComponent } from 'shared/components/context-aware-sidebar/context-aware-sidebar.component';
import { ListingComparisonBannerComponent } from 'shared/components/listing-comparison-banner/listing-comparison-banner.component';
import { LoaderComponent } from 'shared/components/loader/loader.component';
import { ModalComponent } from 'shared/components/modal/modal.component';
import { ToastComponent } from 'shared/components/toast/toast.component';
import { GlobalErrorHandler } from 'shared/error-handlers/globalErrorHandler';
import { CanCreateOutlet } from 'shared/guards';
import { RequirePasswordChangeGuard } from 'shared/guards/require-password-change.guard';
import { ResourcePermissionsGuard } from 'shared/guards/resource-permissions.guard';
import { SharedModule } from 'shared/shared.module';
import { VisualsManagementApiInterceptor } from 'visuals/interceptors/visualsManagementApi.interceptor';
import { environment } from '../environments/environment';

const apolloFactory = (httpLink: HttpLink): NamedOptions => {
    return {
        usersAPI: {
            cache: new InMemoryCache({
                dataIdFromObject(responseObject) {
                    switch (responseObject.__typename) {
                        case 'UserGroupResponse': // @ts-ignore
                            return `UserGroupResponse:${responseObject?.search?.data[0]?.supplierId}`;
                        case 'UserQueries': // @ts-ignore
                            return `UserQueries:${responseObject?.search?.data[0]?.id}`;
                        case 'SupplierUserQueries': // @ts-ignore
                            return `SupplierUserQueries:${responseObject?.search?.data[0]?.id}`;
                        default:
                            return defaultDataIdFromObject(responseObject);
                    }
                },
            }),
            link: httpLink.create({ uri: environment.usersGraphQLURL }),
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'no-cache',
                },
                query: {
                    fetchPolicy: 'no-cache',
                },
            },
        },
        listingAPI: {
            cache: new InMemoryCache(),
            link: ApolloLink.from([
                new BypassErrorLink(BYPASS_ERROR_HANDLER_FOR_GRAPHQL_ERROR_CODES),
                httpLink.create({ uri: environment.listingGraphQLURL }),
            ]),
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'no-cache',
                },
                query: {
                    fetchPolicy: 'no-cache',
                },
            },
        },
        suppliersAPI: {
            cache: new InMemoryCache({
                dataIdFromObject(responseObject) {
                    switch (responseObject.__typename) {
                        case 'SupplierQueries': // @ts-ignore
                            return `SupplierQueries:${responseObject?.searchSuppliers?.data[0]?.supplierId}`;
                        default:
                            return defaultDataIdFromObject(responseObject);
                    }
                },
            }),
            link: ApolloLink.from([
                new BypassErrorLink(BYPASS_ERROR_HANDLER_FOR_GRAPHQL_ERROR_CODES),
                httpLink.create({ uri: environment.suppliersGraphQLURL }),
            ]),
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'no-cache',
                },
                query: {
                    fetchPolicy: 'no-cache',
                },
            },
        },
        distributionManagementAPI: {
            cache: new InMemoryCache(),
            link: ApolloLink.from([
                new BypassErrorLink(BYPASS_ERROR_HANDLER_FOR_GRAPHQL_ERROR_CODES),
                httpLink.create({ uri: environment.distributionManagementGraphQLURL }),
            ]),
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'no-cache',
                },
                query: {
                    fetchPolicy: 'no-cache',
                },
            },
        },
    };
};
LicenseManager.setLicenseKey(
    'Using_this_{AG_Grid}_Enterprise_key_{AG-102677}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Shiji_(US),_Inc.}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{contenthub}_only_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{contenthub}_need_to_be_licensed___{contenthub}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{30_July_2027}____[v3]_[01]_MTgxNjkwMjAwMDAwMA==c3d072e2f46c81e77070a346ec4b8180',
);

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        NgIdleModule.forRoot(),
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        SharedModule,
        DashboardModule,
        AuthenticationModule,
        HeaderComponent,
        SidebarComponent,
        ModalComponent,
        ToastComponent,
        LoaderComponent,
        NgOptimizedImage,
        NgxPermissionsModule.forRoot(),
        ContextAwareSidebarComponent,
        ListingComparisonBannerComponent,
    ],
    providers: [
        AuthGuard,
        AnonymousGuard,
        ResourcePermissionsGuard,
        CanCreateOutlet,
        RequirePasswordChangeGuard,
        Title,
        TokenInterceptor,
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: environment.BASE_URL + 'globalConfigApi/translations?languageIsoCode=',
                suffix: `&cacheBuster=${Date.now()}`,
            }),
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideNamedApollo(() => apolloFactory(inject(HttpLink))),
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: HTTP_INTERCEPTORS, useClass: VisualsManagementApiInterceptor, multi: true },
    ],
})
export class AppModule {}
